import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import axios from 'axios';
import {
    Box, Paper, Typography, Button, TextField, CircularProgress,
    Alert, Chip, Divider, InputAdornment, IconButton
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import CloudIcon from '@mui/icons-material/Cloud';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

function formatBytes(bytes) {
    if (!bytes) return '0 o';
    const k = 1024;
    const sizes = ['o', 'Ko', 'Mo', 'Go'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function FileIcon({ mimetype }) {
    const sx = { fontSize: 56 };
    if (!mimetype) return <ArticleIcon sx={{ ...sx, color: '#757575' }} />;
    if (mimetype === 'application/pdf') return <PictureAsPdfIcon sx={{ ...sx, color: '#a20000' }} />;
    if (mimetype.startsWith('image/')) return <ImageIcon sx={{ ...sx, color: '#1e88e5' }} />;
    if (mimetype.includes('word') || mimetype.includes('document') || mimetype.includes('text/'))
        return <DescriptionIcon sx={{ ...sx, color: '#43a047' }} />;
    if (mimetype.includes('zip') || mimetype.includes('rar') || mimetype.includes('compressed'))
        return <FolderZipIcon sx={{ ...sx, color: '#fb8c00' }} />;
    return <ArticleIcon sx={{ ...sx, color: '#757575' }} />;
}

export default function SharePage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [password, setPassword] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [pwdError, setPwdError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [downloaded, setDownloaded] = useState(false);

    const fetchInfo = async (pwd = '') => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (pwd) params.pwd = pwd;
            const res = await axios.get(`${API_URL}/api/drive/public/share/${token}`, { params });
            setInfo(res.data);
            if (res.data.password_required && pwd) {
                setPwdError('Mot de passe incorrect');
            } else {
                setPwdError(null);
            }
        } catch (e) {
            const msg = e.response?.data?.error || 'Lien invalide ou expiré';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchInfo(); }, [token]);

    const handlePasswordSubmit = () => {
        if (!password.trim()) return;
        fetchInfo(password);
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const params = {};
            if (password) params.pwd = password;
            const res = await axios.get(
                `${API_URL}/api/drive/public/share/${token}/download`,
                { params, responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = info?.nom_original || 'fichier';
            a.click();
            window.URL.revokeObjectURL(url);
            setDownloaded(true);
        } catch (e) {
            const msg = e.response?.data?.error || 'Erreur lors du téléchargement';
            setPwdError(msg);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CloudIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    Drive — Partage sécurisé
                </Typography>
            </Box>

            <Paper variant="outlined" sx={{ maxWidth: 480, width: '100%', p: 4, borderRadius: 2 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: 'primary.main' }} />
                    </Box>
                )}

                {!loading && error && (
                    <Alert severity="error">{error}</Alert>
                )}

                {!loading && info && info.password_required && (
                    <Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                            <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                            <Typography variant="h6" fontWeight="bold">Fichier protégé</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {info.nom_original}
                            </Typography>
                        </Box>
                        <TextField
                            fullWidth
                            label="Mot de passe"
                            type={showPwd ? 'text' : 'password'}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handlePasswordSubmit()}
                            error={!!pwdError}
                            helperText={pwdError}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPwd(v => !v)} edge="end">
                                            {showPwd ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ mb: 2 }}
                        />
                        <Button
                            fullWidth variant="contained"
                            onClick={handlePasswordSubmit}
                            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}
                        >
                            Accéder
                        </Button>
                    </Box>
                )}

                {!loading && info && !info.password_required && (
                    <Box>
                        {/* Security warning */}
                        <Alert
                            severity="warning"
                            icon={<WarningAmberIcon fontSize="small" />}
                            sx={{ mb: 3, borderRadius: 2 }}
                        >
                            <Typography variant="caption">
                                Ce lien est <strong>accessible sans connexion</strong>
                                {info.mot_de_passe_hash ? ' — protégé par mot de passe uniquement' : ' — aucune protection'}.
                                Cela est moins sécurisé qu'un partage par compte.
                            </Typography>
                        </Alert>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                            <FileIcon mimetype={info.mimetype} />
                            <Typography variant="h6" fontWeight="bold" sx={{ mt: 1, textAlign: 'center', wordBreak: 'break-word' }}>
                                {info.nom_original}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                                <Chip label={formatBytes(info.taille)} size="small" />
                                {info.mimetype && <Chip label={info.mimetype.split('/')[1]?.toUpperCase() || info.mimetype} size="small" />}
                            </Box>
                        </Box>

                        {info.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                                {info.description}
                            </Typography>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 3 }}>
                            {info.uploaded_by && (
                                <Typography variant="caption" color="text.secondary">
                                    Partagé par : {info.uploaded_by}
                                </Typography>
                            )}
                            {info.date_upload && (
                                <Typography variant="caption" color="text.secondary">
                                    Déposé le : {new Date(info.date_upload).toLocaleDateString('fr-FR')}
                                </Typography>
                            )}
                            {info.expiration && (
                                <Typography variant="caption" color={new Date(info.expiration) < new Date() ? 'error' : 'text.secondary'}>
                                    Expire le : {new Date(info.expiration).toLocaleDateString('fr-FR')}
                                </Typography>
                            )}
                            {info.limite_acces && (
                                <Typography variant="caption" color="text.secondary">
                                    Téléchargements : {info.nb_acces} / {info.limite_acces}
                                </Typography>
                            )}
                        </Box>

                        {downloaded && (
                            <Alert severity="success" sx={{ mb: 2 }}>Téléchargement démarré !</Alert>
                        )}

                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                            onClick={handleDownload}
                            disabled={downloading}
                            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}
                        >
                            {downloading ? 'Téléchargement...' : 'Télécharger'}
                        </Button>
                    </Box>
                )}
            </Paper>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                Ce fichier est partagé de manière sécurisée via Drive
            </Typography>
        </Box>
    );
}
